import React from 'react';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type Shape from '../objects/Shape';
import tools from '../tools';
import PlaceholderImageCache from '../utils/images/placeholderImageCache';
import PolygonBase from './PolygonBase';
import imageIcon from '../assets/icons/image.svg';
import { svgToIcon } from '../utils/svg';
import { type ConnectControlWithNormalDefinition } from '../types';
import { standardInboundConnectControlDefinition } from '../utils/connectors';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png'] as const;

// These come from https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
export type colorFilterMode =
  | 'color'
  | 'color-burn'
  | 'color-dodge'
  | 'darken'
  | 'difference'
  | 'exclusion'
  | 'hard-light'
  | 'hue'
  | 'lighten'
  | 'lighter'
  | 'luminosity'
  | 'multiply'
  | 'overlay'
  | 'saturation'
  | 'screen'
  | 'soft-light'
  | 'source-atop';

/** Set an object's width/height, compensating for any change via scale. Used to match an image
 * or video's attributes to its natural dimensions. */
export const normaliseDimensions = (
  obj: fabric.Object,
  width: number,
  height: number
) => {
  if (obj.width !== width || obj.height !== height) {
    const currentScaleX = obj.scaleX ?? 1;
    const currentScaleY = obj.scaleY ?? 1;

    // Calculate individual scale adjustments
    const widthRatio = obj.width ? obj.width / width : 1;
    const heightRatio = obj.height ? obj.height / height : 1;

    const ratio = Math.min(widthRatio, heightRatio);
    obj.set({
      width,
      height,
      scaleX: ratio * currentScaleX,
      scaleY: ratio * currentScaleY,
    });
  }
};

class ShapeImage extends PolygonBase {
  // Cache of images indexed by attachmentId. Note that this is global and
  // will cache across whiteboard instances. This will vastly improve load time
  // and memory if the user duplicates the same image many times, which can be
  // quite common. On the other hand, it has the disadvantage that browsing
  // many whiteboards will cache all of the images. Reloading those whiteboards,
  // like might happen frequently when embedded in a note, will be a lot faster
  // since no server round-trip is necessary at all.
  static imageCache = {};

  static pathString() {
    return `
      M -0.5 -0.5
      L 0.5 -0.5
      L 0.5 0.5
      L -0.5 0.5
      Z
    `;
  }

  static pathStrokeOnly = true;

  static shapeControls = this.createDefaultControls();
  static connectControlDefinition(): ConnectControlWithNormalDefinition {
    return standardInboundConnectControlDefinition(false);
  }

  // Image content.
  image: HTMLImageElement | null = null;

  // Whether the image data is being loaded.
  loading: boolean = false;

  // When using the slice tool, the image is sliced and the original image is
  // replaced with the sliced image. This is a temporary placeholder for the
  // sliced image to show while the new image is being uploaded.
  localPlaceholderImage: HTMLImageElement | null = null;

  constructor(fabricObject: Shape) {
    super(fabricObject);

    this.fabricObject.uniformScaling = true;
    this.fabricObject.allowLinearEdgeScaling = true;
    this.fabricObject.strokeWidth ||= 1;
    this.fabricObject.opacity ||= 1;
    this.fabricObject.stroke ??= null;
    this.fabricObject.fill ||= null;

    if (fabricObject.attachmentId) {
      this.prepareImage();
    }
    PlaceholderImageCache.preparePlaceholder();

    this.fabricObject.on('moved', () => this.updateCroppedImagePosition());
    this.fabricObject.on('scaling', () => {
      this.updateCroppedImageScale();
      this.updateCroppedImagePosition();
    });
    this.fabricObject.on('rotated', () => this.updateCroppedImagePosition());
  }

  updateCroppedImagePosition() {
    const obj = this.fabricObject;
    if (!obj.cropped) return;

    const {
      left,
      top,
      originalWidth,
      originalScaleX,
      croppedHorizontalOffset,
      originalHeight,
      originalScaleY,
      croppedVerticalOffset,
    } = obj;

    const originalScaledWidth = originalWidth * originalScaleX;
    const horizontalOffset =
      originalScaledWidth * (croppedHorizontalOffset as number);

    const originalScaledHeight = originalHeight * originalScaleY;
    const verticalOffset =
      originalScaledHeight * (croppedVerticalOffset as number);

    this.fabricObject.controller.undoManager.withoutUndo(() => {
      obj.set({
        originalLeft: left - horizontalOffset,
        originalTop: top - verticalOffset,
        croppedLeft: obj.left,
        croppedTop: obj.top,
      });
    });
  }

  updateCroppedImageScale() {
    const obj = this.fabricObject;
    if (!this.fabricObject.cropped) return;

    const scaleXDelta = obj.scaleX - (obj.croppedScaleX as number);

    const scaleYDelta =
      obj.scaleY - (this.fabricObject.croppedScaleY as number);

    const scaleXProportion = scaleXDelta / (obj.croppedScaleX as number);
    const scaleYProportion = scaleYDelta / (obj.croppedScaleY as number);

    const newOriginalScaleX = obj.originalScaleX * (1 + scaleXProportion);
    const scaledWidth = obj.originalWidth * newOriginalScaleX;
    const horizontalOffset =
      scaledWidth * (obj.croppedHorizontalOffset as number);

    const newOriginalScaleY = obj.originalScaleY * (1 + scaleYProportion);
    const scaledHeight = obj.originalHeight * newOriginalScaleY;
    const verticalOffset = scaledHeight * (obj.croppedVerticalOffset as number);

    obj.set({
      originalScaleX: newOriginalScaleX,
      originalScaleY: newOriginalScaleY,
      croppedScaleX: obj.scaleX,
      croppedScaleY: obj.scaleY,
      originalLeft: obj.left - horizontalOffset,
      originalTop: obj.top - verticalOffset,
    });
  }

  get originalValues() {
    const obj = this.fabricObject;

    const left = obj.originalLeft ?? obj.left;
    const width = obj.originalWidth ?? obj.width;
    const scaleX = obj.originalScaleX ?? obj.scaleX;
    const scaledWidth = width * scaleX;

    const top = obj.originalTop ?? obj.top;
    const height = obj.originalHeight ?? obj.height;
    const scaleY = obj.originalScaleY ?? obj.scaleY;
    const scaledHeight = height * scaleY;

    return {
      left,
      top,
      width,
      height,
      scaleX,
      scaleY,
      scaledWidth,
      scaledHeight,
    };
  }

  commitCropping(cropValues) {
    const obj = this.fabricObject;

    const {
      left: originalLeft,
      top: originalTop,
      width: originalWidth,
      height: originalHeight,
      scaleX: originalScaleX,
      scaleY: originalScaleY,
      scaledWidth,
      scaledHeight,
    } = this.originalValues;

    obj.set({
      cropped: true,
      left: cropValues.left,
      top: cropValues.top,
      width: cropValues.width,
      height: cropValues.height,
      scaleX: cropValues.scaleX,
      scaleY: cropValues.scaleY,
      croppedLeft: cropValues.left,
      croppedTop: cropValues.top,
      croppedWidth: cropValues.width,
      croppedHeight: cropValues.height,
      croppedScaleX: cropValues.scaleX,
      croppedScaleY: cropValues.scaleY,
      croppedHorizontalOffset: (cropValues.left - originalLeft) / scaledWidth,
      croppedVerticalOffset: (cropValues.top - originalTop) / scaledHeight,
      originalLeft,
      originalTop,
      originalWidth,
      originalHeight,
      originalScaleX,
      originalScaleY,
    });

    obj.connectorAttachments.forEach(({ connector }) =>
      connector.constrainConnector({})
    );
    obj.setCoords();
  }

  static renderIcon() {
    return svgToIcon(imageIcon);
  }

  tools(topLevel = true) {
    return topLevel
      ? [
          [tools.ToolFillColor],
          [tools.ToolBorderColorWithOpacity, tools.ToolStrokeStyle],
          [
            tools.ToolSizeImage,
            tools.ToolRecolorImage,
            tools.ToolCrop,
            tools.ToolSliceImage,
            tools.ToolImageMask,
          ],
          [tools.ToolDownloadImage],
        ]
      : [];
  }

  hasFixedDimensions() {
    return false;
  }

  hasEmbeddedText() {
    return false;
  }

  wasSet(keyOrPath: KeyPath, value: unknown) {
    super.wasSet(keyOrPath, value);

    if (keyOrPath === 'attachmentId') {
      this.prepareImage();
    }
  }

  async prepareImage() {
    if (!this.attachmentId) return;

    // Download the image using the ID.
    if (!ShapeImage.imageCache[this.attachmentId]) {
      let src: string;
      const controller = this.fabricObject.controller;

      if (controller.props.attachmentUrls?.[this.attachmentId]) {
        src = controller.props.attachmentUrls[this.attachmentId];
      } else if (controller.props.resolveAttachmentInfo) {
        const attachment = await controller.props.resolveAttachmentInfo(
          this.attachmentId
        );

        if (!attachment) {
          return;
        }

        src = attachment.url;
      }

      src ||= `/attachments/${this.attachmentId}`;

      ShapeImage.imageCache[this.attachmentId] = this.loadImage(src);
    }

    this.image = ShapeImage.imageCache[this.attachmentId];
  }

  // Since the load is asynchronous we need to augment the original undo entry
  // so that height/width is undone properly.
  loadImage(src: string) {
    const obj = this.fabricObject;
    let imageParams: number[] = [];
    if (obj.cropped) {
      imageParams = [
        obj.originalWidth * obj.originalScaleX,
        obj.originalHeight * obj.originalScaleY,
      ];
    }

    const undoEntry = obj.controller.undoManager.currentUndoEntry();
    const image = new Image(...imageParams);
    image.src = src;
    image.crossOrigin = 'anonymous';

    const finishLoading = (width: number, height: number) => {
      // Set the image to 50% if the source is the encoded image
      // representation (i.e. It was just added and it's uploading)
      this.loading = src.startsWith('data:');

      const setDimensions = () => normaliseDimensions(obj, width, height);
      if (undoEntry) {
        obj.controller.undoManager.addUndo(undoEntry, setDimensions);
      } else {
        setDimensions();
      }

      if (this.localPlaceholderImage && !this.loading) {
        this.localPlaceholderImage = null;
      }

      obj.controller.renderManager.refreshObject(obj);

      obj.setCoords();
      obj.synchronize();
      obj.canvas?.requestRenderAll();
    };

    image
      .decode()
      .then(() => {
        this.image = image;

        finishLoading(image.naturalWidth, image.naturalHeight);
      })
      .catch(error => {
        if (error instanceof DOMException && error.name === 'EncodingError') {
          // fall back to an actual html image element if decoding fails, as it can handle much bigger images

          const el = document.createElement('img');
          el.style.cssText = 'position: absolute; right: 100%;';
          el.onload = (event: Event) => {
            const img = event.target as HTMLImageElement;

            finishLoading(img.naturalWidth, img.naturalHeight);

            document.body.removeChild(el);
          };
          document.body.appendChild(el);
          el.src = src;
        } else {
          console.error(`Failed to load attachment image:`, error);
        }
      });

    return image;
  }

  downloadImage() {
    if (this.image?.src) {
      const link = document.createElement('a');
      link.href = this.image.src;
      link.download = 'image';

      link.click();
    }
  }

  setLocalPlaceholderImageFromBlob = blob => {
    const image = new Image();
    image.src = URL.createObjectURL(blob);
    this.localPlaceholderImage = image;
    this.image = null;
  };

  allowedStyleCopyPasteFields(): string[] {
    return ['fill', 'opacity'];
  }

  hasExternalContent() {
    return this.canvas?.isHTMLExport();
  }

  renderExternalContent() {
    const obj = this.fabricObject;

    if (!obj.cropped) {
      return (
        <img
          src={`/attachments/${this.attachmentId}`}
          // When rendering externally we will be within a container that will
          // be the same size as our shape
          style={{ height: '100%', width: '100%' }}
        />
      );
    }

    // For cropped images, implement the same canvas rendering logic
    const sourceX = obj.originalWidth * (obj.croppedHorizontalOffset ?? 0);
    const sourceY = obj.originalHeight * (obj.croppedVerticalOffset ?? 0);
    const sourceWidth = obj.getScaledWidth() / obj.originalScaleX;
    const sourceHeight = obj.getScaledHeight() / obj.originalScaleY;

    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: `${-(sourceX / sourceWidth) * 100}%`,
            top: `${-(sourceY / sourceHeight) * 100}%`,
            width: `${(obj.originalWidth / sourceWidth) * 100}%`,
            height: `${(obj.originalHeight / sourceHeight) * 100}%`,
          }}
        >
          <img
            src={`/attachments/${this.attachmentId}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'fill',
            }}
          />
        </div>
      </div>
    );
  }

  render(ctx: CanvasRenderingContext2D) {
    const obj = this.fabricObject;
    let image = this.image;

    if (!this.hasExternalContent()) {
      if (this.localPlaceholderImage) {
        image = this.localPlaceholderImage;
      } else if (!image || !image.complete || image.naturalHeight === 0) {
        // The image is not ready, or failed to load. Show a placeholder.
        // `naturalHeight` is checked to see if the image failed to load.
        PlaceholderImageCache.renderPlaceholder(ctx, obj);
        return;
      }

      if (obj.fill) {
        ctx.save();
        ctx.fillStyle = obj.fill;
        ctx.fillRect(-obj.width / 2, -obj.height / 2, obj.width, obj.height);
        ctx.restore();
      }
    }

    const x = -obj.width / 2;
    const y = -obj.height / 2;

    const isBeingCropped = obj.controller.imageToCrop?.id === obj.id;

    ctx.save();
    if (this.loading) {
      ctx.globalAlpha = 0.5;
    }

    if (image && !this.hasExternalContent()) {
      if (!isBeingCropped) {
        this.renderImageMask(obj, ctx, x, y);
      }

      if (!obj.cropped) {
        ctx.drawImage(image, x, y, obj.width, obj.height);
      } else if (isBeingCropped) {
        const imageWidth = obj.originalWidth * obj.originalScaleX;
        const imageHeight = obj.originalHeight * obj.originalScaleY;

        ctx.drawImage(
          image,
          0,
          0,
          obj.originalWidth,
          obj.originalHeight,
          x -
            (imageWidth / obj.scaleX) * (obj.croppedHorizontalOffset as number),
          y -
            (imageHeight / obj.scaleY) * (obj.croppedVerticalOffset as number),
          imageWidth / obj.scaleX,
          imageHeight / obj.scaleY
        );
      } else {
        ctx.drawImage(
          image,
          obj.originalWidth * (obj.croppedHorizontalOffset as number),
          obj.originalHeight * (obj.croppedVerticalOffset as number),
          obj.getScaledWidth() / obj.originalScaleX,
          obj.getScaledHeight() / obj.originalScaleY,
          x,
          y,
          obj.originalWidth,
          obj.originalHeight
        );
      }
    }

    // Apply colour filter if active
    const { colorFilterColor, colorFilterPreviewColor } = this.fabricObject;
    if (colorFilterPreviewColor || colorFilterColor !== 'transparent') {
      const { colorFilterOpacity, colorFilterMode, colorFilterPreviewMode } =
        this.fabricObject;
      ctx.save();
      ctx.globalCompositeOperation = colorFilterPreviewMode ?? colorFilterMode;
      ctx.fillStyle = colorFilterPreviewColor ?? colorFilterColor;
      ctx.globalAlpha = colorFilterOpacity;
      ctx.fillRect(x, y, obj.width, obj.height);
      ctx.restore();
    }

    ctx.restore();
    if (!obj.maskType || obj.maskType === 'none') {
      this.renderPath(ctx);
    }
    this._setBorderDash(ctx);
    obj._renderStroke(ctx);
  }

  private renderImageMask(
    obj: Shape,
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number
  ) {
    if (obj.maskType === 'circle') {
      ctx.beginPath();
      ctx.ellipse(
        x + obj.width / 2,
        y + obj.height / 2,
        obj.width / 2,
        obj.height / 2,
        0,
        0,
        Math.PI * 2
      );
      ctx.closePath();
      ctx.clip();
    } else if (obj.maskType === 'rounded-rect') {
      const radius = Math.min(obj.width, obj.height) / 10;
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + obj.width - radius, y);
      ctx.quadraticCurveTo(x + obj.width, y, x + obj.width, y + radius);
      ctx.lineTo(x + obj.width, y + obj.height - radius);
      ctx.quadraticCurveTo(
        x + obj.width,
        y + obj.height,
        x + obj.width - radius,
        y + obj.height
      );
      ctx.lineTo(x + radius, y + obj.height);
      ctx.quadraticCurveTo(x, y + obj.height, x, y + obj.height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.clip();
    }
  }
}

export default ShapeImage;
export { ALLOWED_IMAGE_TYPES };
